import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Ticket, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const statusColor = {
  open: "bg-blue-50 text-blue-700 border-blue-200",
  in_progress: "bg-amber-50 text-amber-700 border-amber-200",
  resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  closed: "bg-zinc-100 text-zinc-600 border-zinc-200",
};

const priorityColor = {
  low: "text-zinc-500",
  medium: "text-blue-600",
  high: "text-amber-600",
  urgent: "text-red-600",
};

export default function TicketsPage() {
  const [tickets, setTickets] = useState([]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    api.get("/tickets").then((r) => setTickets(r.data)).finally(() => setLoading(false));
  }, []);

  const confirmDelete = async () => {
    if (!confirmTarget || deleting) return;
    setDeleting(true);
    try {
      if (confirmTarget === "all") {
        const { data } = await api.delete("/tickets");
        setTickets([]);
        toast.success(`All tickets deleted (${data.deleted_count} removed)`);
      } else {
        await api.delete(`/tickets/${confirmTarget}`);
        setTickets((prev) => prev.filter((t) => t.id !== confirmTarget));
        toast.success("Ticket permanently deleted");
      }
    } catch {
      toast.error("Delete failed. Please try again.");
    } finally {
      setDeleting(false);
      setConfirmTarget(null);
    }
  };

  const filtered = tickets.filter((t) => {
    if (filter !== "all" && t.status !== filter) return false;
    if (query && !t.title.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="p-8 lg:p-12 max-w-6xl" data-testid="tickets-page">
      <div className="flex items-end justify-between mb-10 fade-up">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-blue-600 mb-3">Support tickets</div>
          <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl text-zinc-900">My tickets</h1>
        </div>
        <div className="flex items-center gap-3">
          {tickets.length > 0 && (
            <button
              onClick={() => setConfirmTarget("all")}
              className="inline-flex items-center gap-2 border-2 border-red-200 text-red-600 text-sm font-bold px-5 py-3 rounded-full hover:bg-red-50 hover:border-red-400 transition-all"
              data-testid="delete-all-tickets-btn"
            >
              <Trash2 className="w-4 h-4" /> Delete all
            </button>
          )}
          <Link
            to="/app/tickets/new"
            className="inline-flex items-center gap-2 bg-zinc-900 text-white text-sm font-bold px-5 py-3 rounded-full hover:bg-zinc-800 transition-all hover:-translate-y-0.5"
            data-testid="new-ticket-btn"
          >
            <Plus className="w-4 h-4" /> New ticket
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-zinc-400 absolute left-4 top-1/2 -translate-y-1/2" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search tickets..."
            className="w-full pl-11 pr-4 py-3 rounded-xl border-2 border-zinc-200 bg-white text-sm focus:border-blue-600 focus:outline-none"
            data-testid="tickets-search"
          />
        </div>
        <div className="flex gap-1.5 bg-white rounded-xl border border-zinc-200 p-1">
          {["all", "open", "in_progress", "resolved", "closed"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition ${
                filter === f ? "bg-zinc-900 text-white" : "text-zinc-500 hover:text-zinc-900"
              }`}
              data-testid={`filter-${f}`}
            >
              {f.replace("_", " ")}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-16 text-zinc-400 text-sm">Loading tickets…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-3xl border border-zinc-200 p-16 text-center" data-testid="tickets-empty">
          <Ticket className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
          <h3 className="font-display font-bold text-xl text-zinc-900 mb-2">No tickets to show</h3>
          <p className="text-zinc-500 mb-6">
            {tickets.length === 0
              ? "You haven't created any tickets yet. Most IT issues can be solved instantly via the AI Assistant."
              : "No tickets match your filter."}
          </p>
          <Link
            to="/app/tickets/new"
            className="inline-flex items-center gap-2 bg-zinc-900 text-white font-bold px-5 py-2.5 rounded-full hover:bg-zinc-800 transition"
          >
            <Plus className="w-4 h-4" /> Create your first ticket
          </Link>
        </div>
      ) : (
        <div className="bg-white rounded-3xl border border-zinc-200 overflow-hidden">
          {filtered.map((t) => (
            <Link
              key={t.id}
              to={`/app/tickets/${t.id}`}
              className="flex items-center gap-4 p-5 border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50 transition"
              data-testid={`ticket-row-${t.id}`}
            >
              <div className="w-1 h-12 rounded-full bg-zinc-200" />
              <div className="flex-1 min-w-0">
                <div className="font-bold text-zinc-900 truncate">{t.title}</div>
                <div className="flex items-center gap-3 text-xs text-zinc-500 mt-1">
                  <span className="font-mono uppercase tracking-wider">{t.id.slice(0, 8)}</span>
                  <span>·</span>
                  <span>{t.category}</span>
                  <span>·</span>
                  <span className={`font-bold uppercase tracking-wider ${priorityColor[t.priority]}`}>{t.priority}</span>
                  <span>·</span>
                  <span>{new Date(t.created_at).toLocaleDateString()}</span>
                </div>
              </div>
              <span className={`text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full border ${statusColor[t.status]}`}>
                {t.status.replace("_", " ")}
              </span>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setConfirmTarget(t.id);
                }}
                title="Delete this ticket"
                className="p-2.5 rounded-full text-zinc-400 hover:text-red-600 hover:bg-red-50 transition"
                data-testid={`delete-ticket-btn-${t.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </Link>
          ))}
        </div>
      )}

      <AlertDialog open={!!confirmTarget} onOpenChange={(o) => !o && setConfirmTarget(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmTarget === "all" ? "Delete ALL tickets?" : "Delete this ticket?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmTarget === "all"
                ? `This will permanently remove all ${tickets.length} tickets from the database. This cannot be undone.`
                : "This ticket will be permanently removed from the database. This cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-confirm-btn"
            >
              {deleting ? "Deleting…" : "Yes, delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
